import React, { useState } from "react"
import { Alert, Button, ControlLabel, FormControl, FormGroup, Glyphicon, Modal } from "react-bootstrap"
import axios from "axios"
import getServerURL from "../../../../functions/getServerURL"
import swal from "sweetalert"
import { handleErrorFetchingData } from "../../../../functions/errorHandling"
import { v4 as uuidv4 } from "uuid"


export default function CreateEditTrainingModal({ getTrainings, mode="create", training=null }) {
  // STATE
  const [show, toggleShow] = useState(false)
  const [type, setType] = useState(training ? training.type : "")
  const [number, setNumber] = useState(training ? training.number : "")
  const [checkingNumberAvailability, setCheckingNumberAvailability] = useState(false)
  const [isNumberValid, setIsNumberValid] = useState(null)
  const [title, setTitle] = useState(training ? training.title : "")
  const [version, setVersion] = useState(training ? training.version : "")
  const [trainingDocuments, setTrainingDocuments] = useState(training ? [...training.training_documents] : [])
  const [errorMessage, setErrorMessage] = useState("")
  const [errorsObject, setErrorsObject] = useState({})

  const handleCancel = () => {
    toggleShow(false)
    setCheckingNumberAvailability(false)
    setIsNumberValid(null)
    setType(training ? training.type : "")
    setNumber(training ? training.number : "")
    setTitle(training ? training.title : "")
    setVersion(training ? training.version : "")
    // setTrainingMaterials([])
    setTrainingDocuments(training ? [...training.training_documents] : [])
    setErrorMessage("")
    setErrorsObject({})
  }

  const checkNumberAvailability = (selectedType, selectedNumber, selectedVersion) => {
    setIsNumberValid("loading")
    setCheckingNumberAvailability(true)
    axios.get(`${getServerURL()}trainings/check_number_availability/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
      params: { type: selectedType, number: selectedNumber, version: selectedVersion, id: training ? training.id : null }
    }).then(response => {
      if (response.data.message === "Currently selected training") {
        setIsNumberValid(null)
      } else {
        setIsNumberValid("success")
      }
      setCheckingNumberAvailability(false)
    }).catch(error => {
      setIsNumberValid("error")
      console.log(error)
    })
  }

  const handleChangeType = (e) => {
    let selectedType = e.target.value
    setType(selectedType)
    if (number) {
      checkNumberAvailability(selectedType, number, version)
    }
  }

  const handleCheckNumberAvailability = (selectedType = type, selectedNumber = number, selectedVersion = version) => {
    if (selectedType && selectedNumber && selectedVersion) {
      checkNumberAvailability(selectedType, selectedNumber, selectedVersion)
    }
  }

  const handleFormatNumber = (e) => {
    let formattedNumber = number.padStart(3,"0")
    setNumber(formattedNumber)
    handleCheckNumberAvailability(type, formattedNumber, version)
  }

  const handleChangeVersion = (e) => {
    setVersion(e.target.value)
    handleCheckNumberAvailability(type, number, e.target.value)
  }

  const handleAddTrainingDocument = () => {
    const updatedTrainingDocuments = [ ...trainingDocuments, { id: uuidv4(), name: "", document_url: "" } ]
    setTrainingDocuments(updatedTrainingDocuments)
  }

  const handleEditTrainingDocument = (index, field, value) => {
    const updatedTrainingDocuments = [ ...trainingDocuments ]
    updatedTrainingDocuments[index][field] = value
    setTrainingDocuments(updatedTrainingDocuments)
  }

  const handleRemoveTrainingDocument = (index) => {
    let updatedTrainingDocuments = [ ...trainingDocuments ]
    updatedTrainingDocuments.splice(index, 1)
    setTrainingDocuments(updatedTrainingDocuments)
  }

  const handleSubmit = () => {
    setErrorMessage("")
    if (!type || !number || !title || !version || !trainingDocuments.length) {
      setErrorMessage("Please fill out all required fields.")
      return
    }
    let errors = {}
    trainingDocuments.forEach(trainingDocument => {
      if (!trainingDocument.name) {
        if (!errors[trainingDocument.id]) {
          errors[trainingDocument.id] = {}
        }
        errors[trainingDocument.id]["name"] = "Please input the document name."
      }
      if (!trainingDocument.document_url) {
        if (!errors[trainingDocument.id]) {
          errors[trainingDocument.id] = {}
        }
        errors[trainingDocument.id]["document_url"] = "Please input the document URL"
      }
    })
    if (Object.keys(errors).length) {
      setErrorsObject(errors)
      setErrorMessage("Please fill out all document fields.")
      return
    } else {
      setErrorsObject({})
    }

    let trainingObject = {
      title,
      type,
      number,
      version: parseInt(version, 0),
      training_documents: trainingDocuments,
    }
    if (training) trainingObject['id'] = training.id

    console.log(trainingObject)
    swal({
      text: `${ mode === "create" ? "Creating" : "Updating" } the training... Please stand by.`,
      icon: require("../../../../static/images/spinner.gif"),
      button: false
    })

    axios({
      method: mode === "create" ? "POST" : "PATCH",
      url: mode === "create" ? `${getServerURL()}trainings/` : training.url,
      data: trainingObject,
      headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
    }).then(response => {
      console.log(response)
      swal({
        title: "Success",
        text: `Training ${title} has been ${ mode === "create" ? "created" : "updated" }`,
        icon: "success",
        button: "Continue",
      }).then(() => {
        getTrainings(1)
      })
    }).catch(error => {
      console.log(error.response)
      const message = `There was an error ${ mode === "create" ? "creating" : "updating" } the training`
      handleErrorFetchingData(error, message)
    })
  }

  return (
    <React.Fragment>
      <Button onClick={() => toggleShow(true)}>{ mode === "create" ? "New" : "Edit" } Training</Button>
      <Modal show={ show } onHide={ handleCancel }>
        <Modal.Header closeButton>
          <Modal.Title>
            { mode === "create" ? "New" : "Edit" } Training
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="new-training-form">
          <div>
            <h5>Type: <span className="required">*</span></h5>
            <FormControl
              componentClass="select"
              id="formControlProjectTitle"
              value={ type }
              onChange={ handleChangeType }
            >
              <option disabled value="">Select a type...</option>
              <option value="GD">GD</option>
              <option value="POL">POL</option>
              <option value="SOP">SOP</option>
              <option value="WI">WI</option>
            </FormControl>
          </div>
          <div>
            <h5>Number: <span className="required">*</span></h5>
            <FormGroup validationState={ isNumberValid !== "loading" ? isNumberValid : null }>
              <FormControl
                id="formControlNumber"
                type="number"
                min="1"
                value={ number }
                onBlur={ handleFormatNumber }
                onChange={ (e) => setNumber(e.target.value) }
                placeholder="Enter a number"
              />
              { isNumberValid === "loading" ?
                <FormControl.Feedback>
                  <Glyphicon
                    glyph='refresh'
                    style={{minWidth: '170px'}}
                    className='rotate-btn'
                  />
                </FormControl.Feedback>
                :
                <FormControl.Feedback />
              }
              {/*{ isNumberValid === "error" &&*/}
              {/*  <ControlLabel>Type/Number/Version combination already taken.</ControlLabel>*/}
              {/*}*/}
            </FormGroup>
          </div>
          <div>
            <h5>Version: <span className="required">*</span></h5>
            <FormGroup validationState={ isNumberValid !== "loading" ? isNumberValid : null }>
              <FormControl
                id="formControlNumber"
                type="number"
                min="1"
                value={ version }
                onBlur={ handleChangeVersion }
                onChange={ (e) => setVersion(e.target.value) }
                placeholder="Enter a version number"
              />
              { isNumberValid === "loading" ?
                <FormControl.Feedback>
                  <Glyphicon
                    glyph='refresh'
                    style={{minWidth: '170px'}}
                    className='rotate-btn'
                  />
                </FormControl.Feedback>
                :
                <FormControl.Feedback />
              }
              { isNumberValid === "error" &&
                <React.Fragment>
                  <br/>
                 <ControlLabel>Type/Number/Version combination already taken.</ControlLabel>
                </React.Fragment>
              }
            </FormGroup>
          </div>
          <div>
            <h5>Title: <span className="required">*</span></h5>
            <FormControl
              id="formControlProjectTitle"
              value={ title }
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title"
            />
          </div>
          <div>
            <h5>Training Materials: <span className="required">*</span></h5>
            <table className="data-source-form-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  {trainingDocuments.length ?
                    <React.Fragment>
                      <th><h5>Name</h5></th>
                      <th><h5>URL</h5></th>
                      <th></th>
                    </React.Fragment> :
                  null }
                </tr>
              </thead>
              <tbody className="training-documents-table">
                { trainingDocuments.length ?
                  trainingDocuments.map((trainingDocument, index) => (
                    <tr key={index}>
                      <td style={{ padding: "0 5px 5px 0" }}>
                        <FormGroup validationState={
                          errorsObject[trainingDocument.id] && errorsObject[trainingDocument.id]["name"] ? "error" : null
                        }>
                          <FormControl
                            id="formControlProjectTitle"
                            value={trainingDocument.name}
                            onChange={(e) => handleEditTrainingDocument(index, "name", e.target.value)}
                            placeholder="Enter a name"
                          />
                        </FormGroup>
                      </td>
                      <td style={{ padding: "0 0 5px 0" }}>
                        <FormGroup validationState={
                          errorsObject[trainingDocument.id] && errorsObject[trainingDocument.id]["document_url"] ? "error" : null
                        }>
                          <FormControl
                            id="formControlProjectTitle"
                            value={trainingDocument.document_url}
                            onChange={(e) => handleEditTrainingDocument(index, "document_url", e.target.value)}
                            placeholder="Enter a url"
                          />
                        </FormGroup>
                      </td>
                        <td style={{ padding: "0 0 5px 5px" }}>
                          <FormGroup>
                            <Button
                              bsStyle="danger"
                              style={{ width: "100%", margin: "0" }}
                              onClick={() => handleRemoveTrainingDocument(index)}
                            >
                              <Glyphicon glyph="remove"/>
                            </Button>
                          </FormGroup>
                        </td>
                    </tr>
                  )) : <tr><td colSpan="2">There are no available training materials</td></tr>
                }
              </tbody>
            </table>
            <div><Button onClick={ handleAddTrainingDocument }>Add Training Material</Button></div>
          </div>
          { errorMessage &&
            <React.Fragment>
              <br/>
              <Alert bsStyle="danger">{ errorMessage }</Alert>
            </React.Fragment>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={ handleCancel }>Cancel</Button>
          <Button bsStyle="primary" disabled={ checkingNumberAvailability } onClick={ handleSubmit }>Submit</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/TrainingContainer/CreateEditTrainingModal.js